import React, { useContext, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { useMutation } from '@apollo/client';
import changeAddressMutation from 'ggApp/shared/context/Order/services/cartServiceAPI/address/addressChange.graphql';

import useMount from 'ggApp/shared/hooks/useMount';
import { isFunction } from 'ggApp/utils/function';
import { PureTranslator } from 'ggApp/modules/translator/components';

import { registerHomeAddressTest } from 'ggApp/modules/newCheckout/tracking';

import { OrderContext } from 'ggApp/shared/context/Order/OrderContext';
import { InfoCard, CARD_TYPE } from 'ggApp/modules/newCheckout/components/InfoCard';
import { CHECKOUT_STEP_NAMES as STEP_NAMES } from 'ggApp/modules/newCheckout/constants';

import { getDefaultAddress } from 'ggApp/modules/newCheckout/components/CheckoutStep/steps/AddressComponent/defaultAddress';
import { address2faFeatureFlag as address2faFeatureFlagSelector } from 'ggApp/modules/featureFlag/selectors';

import Plus from 'ggApp/shared/icons/CleanPlus';
import { ButtonV2 } from '@getgrover/ui';
import { List, AddLabel, StepButtons } from './styles';

export const swapAddressStep = 'SWAP_ADDRESS';

const Component = ({
    stepName,
    addTK,
    setHasAddresses,
    setAddressInForm,
    flowName,
    onSubmitClick,
    address2faFeatureFlag,
    send,
    current,
}) => {
    const { countryName, countryId, activeStore } = useContext(OrderContext);

    const defaultAddress = getDefaultAddress(countryName, countryId);

    const addressType =
        stepName === STEP_NAMES.SHIPPING_ADDRESS
            ? 'is_current_ship_address?'
            : 'is_current_bill_address?';

    const [tempAddress, setTempAddress] = useState(defaultAddress);
    const [addressList, setAddressList] = useState(current.context?.userAddresses ?? []);
    const [activeCard, setActiveCard] = useState();

    const canGoBack =
        current.event.type === 'EDIT_SHIPPING_ADDRESS' ||
        current.event.type === 'EDIT_HOME_ADDRESS' ||
        current.context.inEditMode;

    const onBackClick = useCallback(() => {
        send('BACK_TO_ORDER_REVIEW');
    }, [send]);

    useMount(() => {
        if (addressList.length) {
            if (
                stepName === STEP_NAMES.SHIPPING_ADDRESS &&
                current.event.type !== 'EDIT_SHIPPING_ADDRESS'
            ) {
                const hasDefault = addressList.some(
                    (address) => address['is_current_ship_address?'],
                );

                if (!hasDefault) {
                    onSubmitClick(addressList[0]);
                }
            }

            const selectedAddress =
                addressList.find((address) => address[addressType]) || addressList[0];

            setAddressList([
                selectedAddress,
                ...addressList.filter((a) => a.id !== selectedAddress.id),
            ]);

            setActiveCard(selectedAddress.id);
            setTempAddress(selectedAddress);
        } else if (isFunction(setHasAddresses)) {
            setHasAddresses(false);
        }
    });

    const onCardClick = useCallback((address) => {
        setActiveCard(address.id);
        setTempAddress(address);
    }, []);

    const handleAddClick = useCallback(() => {
        setTempAddress(defaultAddress);
        setAddressInForm(defaultAddress);

        send('EDIT');
    }, [send, defaultAddress, setAddressInForm]);

    const onEditClick = useCallback(
        (address) => {
            setAddressInForm(address);
            send('EDIT');
        },
        [send, setAddressInForm],
    );

    const [callMutation] = useMutation(changeAddressMutation);

    const onContinue = useCallback(
        async (address) => {
            if (stepName === STEP_NAMES.SHIPPING_ADDRESS) {
                registerHomeAddressTest(flowName);
            }

            try {
                if (stepName === swapAddressStep) {
                    await callMutation({
                        variables: {
                            orderNumber: current.context.orderNumber,
                            input: {
                                address: {
                                    type: 'shipping',
                                    countryIso: activeStore.country_code,
                                    country: address.country_name,
                                    line1: address.address1,
                                    additionalInfo: address.additional_info,
                                    line2: address.address2,
                                    city: address.city,
                                    id: address.id,
                                    zip: address.zipcode,
                                },
                            },
                        },
                    });
                }
                send('CHANGE_ADDRESS', {
                    data: [address],
                    addressType: stepName === STEP_NAMES.HOME_ADDRESS ? 'billing' : 'shipping',
                });
            } catch (e) {
                console.error(e);
            }
        },
        [stepName, flowName, send, current, callMutation, activeStore.country_code],
    );

    return (
        <>
            <List data-testid="address-list">
                {addressList &&
                    !!addressList.length &&
                    addressList.map((address) => (
                        <InfoCard
                            key={`item-${address.id}`}
                            setActive={() => onCardClick(address)}
                            type={CARD_TYPE.address}
                            active={activeCard === address.id}
                            onClick={() => onEditClick(address)}
                            address={{
                                id: address.id || null,
                                address1: address.address1 || null,
                                address2: address.address2 || null,
                                city: address.city || null,
                                country: address.country_name || null,
                                zip: address.zipcode || null,
                                countryCode: address.country_id || null,
                                additionalInfo: address.additional_info || null,
                            }}
                            readOnly
                        />
                    ))}

                {address2faFeatureFlag && (
                    <ButtonV2
                        small
                        icon={<Plus />}
                        onClick={handleAddClick}
                        data-testid="address-list-add"
                    >
                        <AddLabel>
                            <PureTranslator tk={addTK} />
                        </AddLabel>
                    </ButtonV2>
                )}
            </List>

            <StepButtons>
                {canGoBack && (
                    <ButtonV2 outlined onClick={onBackClick}>
                        <PureTranslator tk="NEW_CHECKOUT_HOME_BACK_BTN" />
                    </ButtonV2>
                )}

                <ButtonV2
                    fullWidth
                    isDisabled={!activeCard || Object.keys(tempAddress).length === 0}
                    onClick={() => onContinue(tempAddress)}
                    data-testid="address-list-continue"
                >
                    <PureTranslator tk="NEW_CHECKOUT_HOME_CONTINUE_BTN" />
                </ButtonV2>
            </StepButtons>
        </>
    );
};

export const AddressList = connect(
    createStructuredSelector({
        address2faFeatureFlag: address2faFeatureFlagSelector,
    }),
)(Component);
